import type { CSSProperties } from 'react';

// Zdieľané inline štýly + pomocné funkcie naprieč stránkami (Blok 4 — rozdelenie
// pôvodného jednostránkového App.tsx na routované stránky).

export const inputStyle: CSSProperties = {
  padding: '0.55rem 0.8rem',
  marginRight: '0.5rem',
  border: '1px solid var(--line)',
  borderRadius: 8,
  fontSize: '0.9rem',
  background: 'var(--bg)',
  color: 'var(--ink)',
};
// Primárne CTA (Column spec): plné indigo navy pozadie, biely text.
export const buttonStyle: CSSProperties = {
  padding: '0.55rem 1.1rem',
  border: 'none',
  background: 'var(--brand)',
  color: '#ffffff',
  borderRadius: 8,
  fontSize: '0.9rem',
  fontWeight: 500,
  cursor: 'pointer',
};
export const personResultStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '0.6rem 0.7rem',
  border: 'none',
  borderRadius: 8,
  borderBottom: '1px solid var(--line)',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: '0.9rem',
};
export const tableStyle: CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  fontSize: '0.85rem',
};
export const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.6rem',
  borderBottom: '1px solid var(--line)',
  color: 'var(--slate)',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
};
export const tdStyle: CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderTop: '1px solid var(--line)',
};

export function riskBand(score: number): 'low' | 'mid' | 'high' {
  if (score >= 50) return 'high';
  if (score >= 20) return 'mid';
  return 'low';
}
export function riskWord(score: number): string {
  if (score >= 50) return 'Vysoké riziko';
  if (score >= 20) return 'Stredné riziko';
  return 'Nízke riziko';
}
export function signalSeverity(points: number): 'low' | 'mid' | 'high' {
  if (points >= 30) return 'high';
  if (points >= 15) return 'mid';
  return 'low';
}
export function delta(
  current?: number | null,
  previous?: number | null,
): { pct: string; dir: 'up' | 'down' } | null {
  if (current == null || previous == null || previous === 0) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  return { pct: Math.abs(pct).toFixed(1), dir: pct >= 0 ? 'up' : 'down' };
}
export function riskColor(score: number): string {
  if (score >= 50) return 'var(--risk-high)';
  if (score >= 20) return 'var(--risk-mid)';
  return 'var(--risk-low)';
}
export function formatEur(value: number | null): string {
  if (value == null) return '—';
  return `${value.toLocaleString('sk-SK')} €`;
}

// Skrátený zápis pre tabuľky (Týž. 27, user feedback 2026-07-19: chce sa
// odlíšiť od konkurencie čitateľnejšími číslami) — "2,5 mil. €" namiesto
// "2 500 000 €". Presná hodnota patrí do title tooltipu na volajúcej strane.
export function formatEurCompact(value: number | null): string {
  if (value == null) return '—';
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (abs >= 1_000_000_000) {
    return `${sign}${(abs / 1_000_000_000).toLocaleString('sk-SK', { maximumFractionDigits: 1 })} mld. €`;
  }
  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toLocaleString('sk-SK', { maximumFractionDigits: 1 })} mil. €`;
  }
  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toLocaleString('sk-SK', { maximumFractionDigits: 0 })} tis. €`;
  }
  return formatEur(value);
}

// Rozšírené finančné ukazovatele (Corpuls redizajn 2026-07-15) — formátovanie
// podľa RatioUnit z @marstein-radar/shared financial-ratios.ts.
export function formatPct(value: number | null): string {
  if (value == null) return '—';
  return `${(value * 100).toFixed(1)} %`;
}
export function formatRatio(value: number | null): string {
  if (value == null) return '—';
  return `${value.toFixed(2)}×`;
}
export function formatDays(value: number | null): string {
  if (value == null) return '—';
  return `${Math.round(value)} dní`;
}

// Normalizuje IČO vstup (číslice) vs. voľný text (názov) — TopBar rozhoduje, kam navigovať.
export function isIco(input: string): boolean {
  return /^\d{6,10}$/.test(input.trim());
}

// Zaokrúhlené číslo pre landing (user request 2026-07-19: presné počty firiem
// nechceme predávať ako fixné číslo, ktoré starne s každou synchronizáciou).
// Zámerne floor namiesto round, aby "2,1 milióna" nikdy nesľubovalo viac než realita.
export function formatApprox(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value >= 1_000_000) {
    const millions = Math.floor(value / 100_000) / 10;
    return `${millions.toString().replace('.', ',')} milióna`;
  }
  if (value >= 10_000) {
    return `${Math.floor(value / 1000)}-tisíc`;
  }
  if (value >= 1_000) {
    const thousands = Math.floor(value / 100) / 10;
    return `${thousands.toString().replace('.', ',')}-tisíc`;
  }
  return value.toLocaleString('sk-SK');
}
